import { useState, useCallback, useRef, useEffect } from 'react';

import {
    bulkImportMultiPatchAsJob,
    getImportJob,
    cancelImportJob,
    downloadImportJobErrors,
    batchJob as batchJobAPI,
} from 'directory/listings/notification-modals/api';
import { POLL_INTERVAL, IMPORT_JOB_STATE } from 'directory/listings/notification-modals/constants';
import {
    ImportJob,
    ImportJobStatus,
    ImportType,
    Priority,
    LocationDataRow,
    ImportJobState,
    UseImportJobResult,
} from 'directory/listings/notification-modals/types';
import { transformRowsToPatches } from 'directory/listings/notification-modals/utils';

export const useImportJob = (agencyId?: number, tenantId?: number): UseImportJobResult => {
    const [state, setState] = useState<ImportJobState>({
        status: IMPORT_JOB_STATE.IDLE as ImportJobState['status'],
        progress: 0,
        totalRows: 0,
        processedRows: 0,
        successRows: 0,
        errorRows: 0,
        unchangedRows: 0,
        startTime: null,
        elapsedTime: 0,
        importJobId: null,
        jobId: null,
        error: null,
    });

    const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);
    const elapsedTimeIntervalRef = useRef<NodeJS.Timeout | null>(null);

    const clearIntervals = useCallback(() => {
        if (pollingIntervalRef.current) {
            clearInterval(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
        }
        if (elapsedTimeIntervalRef.current) {
            clearInterval(elapsedTimeIntervalRef.current);
            elapsedTimeIntervalRef.current = null;
        }
    }, []);

    const startElapsedTimeTracking = useCallback((startTime: number) => {
        elapsedTimeIntervalRef.current = setInterval(() => {
            setState((prev) => ({
                ...prev,
                elapsedTime: Date.now() - startTime,
            }));
        }, 1000);
    }, []);

    const pollImportJob = useCallback(
        async (importJobId: string) => {
            if (!tenantId) return;

            try {
                const job: ImportJob = await getImportJob(tenantId, importJobId, {
                    includeUsers: true,
                    includeMapping: false,
                });

                const commitStatus = job.commitPhase?.importJobStatus;
                const metrics = job.commitPhase?.importJobMetric?.diffMetric;

                if (metrics) {
                    const { updates, unchanged, errors, failed } = metrics;
                    const processed = (updates || 0) + (unchanged || 0) + (errors || 0) + (failed || 0);

                    setState((prev) => {
                        const progress = prev.totalRows > 0 ? (processed / prev.totalRows) * 100 : 0;

                        return {
                            ...prev,
                            progress: Math.min(progress, 100),
                            processedRows: processed,
                            successRows: updates || 0,
                            unchangedRows: unchanged || 0,
                            errorRows: (errors || 0) + (failed || 0),
                        };
                    });
                }

                if (commitStatus === ImportJobStatus.Completed) {
                    clearIntervals();

                    const totalDuration =
                        (job.validationPhase?.durationMillis || 0) + (job.commitPhase?.durationMillis || 0);

                    setState((prev) => ({
                        ...prev,
                        status: IMPORT_JOB_STATE.COMPLETED as ImportJobState['status'],
                        progress: 100,
                        elapsedTime: totalDuration || prev.elapsedTime,
                    }));
                } else if (commitStatus === ImportJobStatus.Cancelled) {
                    clearIntervals();

                    setState((prev) => ({
                        ...prev,
                        status: IMPORT_JOB_STATE.CANCELLED as ImportJobState['status'],
                    }));
                }
            } catch (error) {
                console.error('Error polling import job:', error);
                clearIntervals();

                setState((prev) => ({
                    ...prev,
                    status: IMPORT_JOB_STATE.ERROR as ImportJobState['status'],
                    error: error instanceof Error ? error : new Error('Unknown error occurred'),
                }));
            }
        },
        [tenantId, clearIntervals],
    );

    const startImportJob = useCallback(
        async (rows: LocationDataRow[], notificationHistoryId?: string) => {
            if (!tenantId) {
                throw new Error('Tenant ID is required');
            }

            try {
                setState((prev) => ({
                    ...prev,
                    status: IMPORT_JOB_STATE.CREATING as ImportJobState['status'],
                    totalRows: rows.length,
                }));

                const patches = transformRowsToPatches(rows);

                if (patches.length === 0) {
                    throw new Error('No valid patches to import');
                }

                const bulkImportRequest = {
                    patches,
                    importType: ImportType.Profile,
                    skipValidation: true,
                    priority: Priority.Normal,
                    parameters: {
                        notificationHistoryID: notificationHistoryId,
                    },
                };

                const response = await bulkImportMultiPatchAsJob(tenantId, bulkImportRequest);
                const { importJobID, job } = response;
                const startTime = Date.now();

                setState((prev) => ({
                    ...prev,
                    status: IMPORT_JOB_STATE.RUNNING as ImportJobState['status'],
                    importJobId: importJobID,
                    jobId: job.id,
                    startTime,
                }));

                startElapsedTimeTracking(startTime);
                pollingIntervalRef.current = setInterval(() => {
                    pollImportJob(importJobID);
                }, POLL_INTERVAL);

                pollImportJob(importJobID);
            } catch (error) {
                console.error('Error starting import job:', error);

                const serverDetail =
                    (error as any)?.response?.data?.error?.detail || (error as any)?.response?.data?.error?.message;
                const normalizedError =
                    error instanceof Error
                        ? new Error(serverDetail || error.message || 'Failed to start import job')
                        : new Error(serverDetail || 'Failed to start import job');

                setState((prev) => ({
                    ...prev,
                    status: IMPORT_JOB_STATE.ERROR as ImportJobState['status'],
                    error: normalizedError,
                }));

                throw normalizedError;
            }
        },
        [tenantId, pollImportJob, startElapsedTimeTracking],
    );

    const cancelJob = useCallback(async () => {
        if (!tenantId || !state.importJobId) {
            throw new Error('Cannot cancel: Import Job ID is missing');
        }

        try {
            await cancelImportJob(tenantId, state.importJobId, {
                commitPhase: true,
            });
            clearIntervals();

            setState((prev) => ({
                ...prev,
                status: IMPORT_JOB_STATE.CANCELLED as ImportJobState['status'],
            }));
        } catch (error) {
            console.error('Error cancelling import job:', error);
            setState((prev) => ({
                ...prev,
                error: error instanceof Error ? error : new Error('Failed to cancel import job'),
            }));
            throw error;
        }
    }, [tenantId, state.importJobId, clearIntervals]);

    const downloadErrors = useCallback(
        async (importJobId?: string) => {
            const jobIdToUse = importJobId;

            if (!agencyId || !tenantId || !jobIdToUse) {
                console.warn('Missing required parameters for downloading errors:', {
                    agencyId,
                    tenantId,
                    importJobId: jobIdToUse,
                });
                throw new Error('Missing required parameters for downloading errors');
            }

            try {
                await downloadImportJobErrors(agencyId, tenantId, jobIdToUse);
            } catch (error) {
                console.error('Error downloading errors:', error);

                const serverDetail =
                    (error as any)?.response?.data?.error?.detail || (error as any)?.response?.data?.error?.message;
                const normalizedError =
                    error instanceof Error
                        ? new Error(serverDetail || error.message || 'Failed to download errors')
                        : new Error(serverDetail || 'Failed to download errors');

                setState((prev) => ({
                    ...prev,
                    error: normalizedError,
                }));

                throw normalizedError;
            }
        },
        [agencyId, tenantId],
    );

    const batchJob = useCallback(async () => {
        if (!tenantId || !state.jobId) {
            throw new Error('Cannot run in background: Job ID is missing');
        }

        try {
            await batchJobAPI(tenantId, state.jobId);
            clearIntervals();
        } catch (error) {
            console.error('Error setting job to batch:', error);
            throw error;
        }
    }, [tenantId, state.jobId, clearIntervals]);

    const reset = useCallback(() => {
        clearIntervals();
        setState({
            status: IMPORT_JOB_STATE.IDLE as ImportJobState['status'],
            progress: 0,
            totalRows: 0,
            processedRows: 0,
            successRows: 0,
            errorRows: 0,
            unchangedRows: 0,
            startTime: null,
            elapsedTime: 0,
            importJobId: null,
            jobId: null,
            error: null,
        });
    }, [clearIntervals]);

    useEffect(() => {
        return () => {
            clearIntervals();
        };
    }, [clearIntervals]);

    return {
        state,
        startImportJob,
        cancelJob,
        downloadErrors,
        batchJob,
        reset,
    };
};
